import { ClaimDocument } from "../../../models/ClaimDocument";
import { ClaimModel } from "../../../models/ClaimModel";
import { BusinessCategories, getFY, PerformanceMeasures, PrincipalAdditionalDocument } from "../../../shared/Utility";
import { calculateGoodsAmount } from "../schedules/Earnings/GoodsEntryDetails";
import { calculateServicesIPRKnowHowAmount } from "../schedules/Earnings/ServicesIPRKnowHowEntryDetails";
import { calculateTourismServicesAmount } from "../schedules/Earnings/TourismServicesEntryDetails";
import { calculateAdvertisingTotal, sortAdvertisingByAmountDesc } from "../schedules/Expenses/AdvertisingEntryDetails";
import { calculateFreeSampleTotal, sortFreeSamplesByAmountDesc } from "../schedules/Expenses/FreeSampleEntryDetails";
import { calculateMarketingConsultantTotal } from "../schedules/Expenses/MarketingConsultantEntryDetails";
import { calculateMarketingVisitTotal, sortMarketingVisitsByAmountDesc } from "../schedules/Expenses/MarketingVisitEntryDetails";
import { calculateRepresentationTotal, sortRepresentationByAmountDesc } from "../schedules/Expenses/RepresentationEntryDetails";

export interface DocumentTypeInfo {
    code: string
    reducer: { (model: ClaimModel, lowRiskMarkets: string[]): string[] }
    title: { (model: ClaimModel, item: string): string }
    description: { (model: ClaimModel, item: string): string }
    required: { (model: ClaimModel, item: string): boolean }
    quickList: { (model: ClaimModel, item: string): boolean }
    multipleFiles: { (model: ClaimModel, item: string): boolean }
    mlClasses: string[]
    moreInfo?: string
}

export const isApplicationForm1DocumentPendingScoring = (document: ClaimDocument): boolean => {
    var docType = documentTypeInfo.filter(r => r.code == document.code);

    if (docType.length > 0) {
        if (docType[0].mlClasses == null)
            return false;

        return document.mlScore == null;
    }

    return false;
}

export const areAnyDocumentsPendingScoring = (claim: ClaimModel, lowRiskMarkets: string[]): boolean => {
    return claim.documents.some(d => {

        if (isApplicationForm1DocumentInfected(d))
            return true;

        var docType = documentTypeInfo.filter(r => r.code == d.code && r.reducer(claim, lowRiskMarkets).length > 0);

        if (docType.length > 0) {
            if (docType[0].mlClasses == null)
                return false;

            return d.mlScore == null;
        }

        return false;
    });
}

export const isApplicationForm1DocumentValid = (document: ClaimDocument): boolean => {
    var docType = documentTypeInfo.filter(r => r.code == document.code);

    if (docType.length > 0) {
        if (docType[0].mlClasses == null)
            return true;

        return docType[0].mlClasses.some(x => x == document.mlCode)
    }

    return true;
}

export const isApplicationForm1DocumentInfected = (document: ClaimDocument): boolean => {
    return document.mlCode == CODE_INFECTED;
}

export const invalidDocumentUploaded = (claim: ClaimModel, lowRiskMarkets: string[]): boolean => {
    return claim.documents.some(d => {
        var docType = documentTypeInfo.filter(r => r.code == d.code && r.reducer(claim, lowRiskMarkets).length > 0);

        if (docType.length > 0) {
            if (docType[0].mlClasses == null)   //not scored - ignore
                return false;

            if (d.mlScore == null)  //Pending scoring
                return false;

            return !docType[0].mlClasses.some(x => x == d.mlCode)
        }

        return false;
    });
}

const requirePrincipalAdditionalDocument = (claim: ClaimModel): boolean => {
    return (!claim.grantsReceived || claim.grantsReceivedCount <= 1) && claim.exportPrincipal;
}

const requireOptionBDocuments = (claim: ClaimModel): boolean => {
    return claim.grantsReceived && claim.grantsReceivedCount >= 2 && claim.performanceMeasure == 2;
}

const requireTrustDeedDocument = (claim: ClaimModel): boolean => {
    return claim.businessCategory == BusinessCategories.Trust && claim.grantsReceived === false;
}

const requirePLAndBSCurrent = (claim: ClaimModel): boolean => {
    return (claim.promotingGoods && !claim.goodsManufacturedInAustralia)
        || (claim.grantsReceived === false)
        || claim.totalIncome > 40000000
        || (claim.grantsReceived && claim.grantsReceivedCount >= 2  //Pre-condition for performance measure
            && claim.performanceMeasure == PerformanceMeasures.OptionA   //Option A = EPT
            && claim.businessCategory != BusinessCategories.ApprovedJointVenture
            && claim.businessCategory != BusinessCategories.ApprovedBody
            && claim.totalIncome < 750000)
        || (claim.claimOverseasRepExpenses  //Pre-condition for os rep expenses
            && claim.representations
                .some(r => {
                    if (r.claimedBefore)
                        return false;

                    let total = calculateRepresentationTotal(r.entries);
                    return total > 40000;
                }));
}

const requireApprovalStatusDocument = (claim: ClaimModel): boolean => {
    return (claim.businessCategory === BusinessCategories.ApprovedJointVenture ||
        claim.businessCategory === BusinessCategories.ApprovedBody);
}

const reducer1A = (claim: ClaimModel, lowRiskMarkets: string[], getMaxValueItem: boolean = false): string[] => {
    if (claim.claimOverseasRepExpenses) {
        return claim.representations.filter(r => {
            if (r.claimedBefore)
                return false;

            let total = calculateRepresentationTotal(r.entries);

            let isInLowRiskMarket = lowRiskMarkets.some(lm => r.physicalAddress && lm == r.physicalAddress.country);

            if (isInLowRiskMarket) {
                if (claim.isQIPConsultant) {
                    if (claim.schedule1ARisk)
                        return total > 140000;
                    else
                        return total > 160000;
                }
                else {
                    if (claim.schedule1ARisk)
                        return total > 50000;
                    else
                        return total > 140000;
                }
            }
            else {
                return total > 40000;
            }

        }).map(r => {
            if (getMaxValueItem) {
                let sortedOSRepExpenses = r.entries.sort((a, b) => sortRepresentationByAmountDesc(a, b));
                if (sortedOSRepExpenses.length > 0)
                    return `${r.id}|${sortedOSRepExpenses.slice(0, 1).map(x => x.id)}`
            }

            return r.id;
        });
    }

    return [];
}

const reducer1B = (claim: ClaimModel): string[] => {
    if (claim.claimMarketingConsultantExpenses) {
        return claim.marketingConsultants.filter(r => {
            let total = calculateMarketingConsultantTotal(r.entries);
            return total > 20000;
        }).map(r => r.id);
    }

    return [];
}

const reducer2 = (claim: ClaimModel): string[] => {
    if (claim.claimMarketingVisitExpenses) {
        let sortedMarketingVisits = claim.marketingVisitEntries.sort((a, b) => sortMarketingVisitsByAmountDesc(a, b));
        let total = calculateMarketingVisitTotal(claim.marketingVisitEntries);
        let itemCount = 0;

        if (claim.isQIPConsultant) {
            if ((total > 40000 && claim.schedule2Risk) || total > 110000)
                itemCount = 1;
        }
        else {  //Non QIP
            if (total > 40000 && claim.schedule2Risk)
                itemCount = 2;
            else if (total > 90000)
                itemCount = 1;
        }

        return sortedMarketingVisits.slice(0, itemCount).map(r => r.id);
    }

    return [];
}

const reducer4 = (claim: ClaimModel): string[] => {
    if (claim.claimFreeSampleExpenses) {
        let sortedFreeSamples = claim.freeSampleEntries.sort((a, b) => sortFreeSamplesByAmountDesc(a, b));
        let total = calculateFreeSampleTotal(claim.freeSampleEntries);
        let areDocumentsRequired = false;

        if (claim.isQIPConsultant) {
            if ((total > 30000 && claim.schedule4Risk) || total > 60000)
                areDocumentsRequired = true;
        }
        else {
            if ((total > 10000 && claim.schedule4Risk) || total > 30000)
                areDocumentsRequired = true;
        }

        if (areDocumentsRequired)
            return sortedFreeSamples.slice(0, 2).map(r => r.id);
    }

    return [];
}

const reducer6 = (claim: ClaimModel): string[] => {
    if (claim.claimPromotionalLiteratureAdvertisingExpenses) {
        let sortedAdvertising = claim.advertisingEntries.sort((a, b) => sortAdvertisingByAmountDesc(a, b));
        let total = calculateAdvertisingTotal(claim.advertisingEntries);
        let areDocumentsRequired = false;

        if (claim.isQIPConsultant) {
            if (total > 10000000)
                areDocumentsRequired = true;
        }
        else {
            if ((total > 40000 && claim.schedule6Risk) || total > 10000000)
                areDocumentsRequired = true;
        }

        if (areDocumentsRequired)
            return sortedAdvertising.slice(0, 2).map(r => r.id);
    }

    return [];
}

const reducer9A = (claim: ClaimModel): string[] => {
    let maxId = null;
    if (claim.claimGoodEarnings) {
        if (claim.grantsReceived && claim.grantsReceivedCount > 1) {
            let maxValue = 0;
            for (let e of claim.goodsEntries) {
                if (e.earningsReceivedMonth != null && e.date == null) continue;
                let amount = calculateGoodsAmount(e);
                if (e.date != null && maxId == null || (amount > maxValue)) {
                    maxValue = amount;
                    maxId = e.id
                }
            }
        }
    }

    return maxId != null ? [maxId] : [];
}

const reducer9B = (claim: ClaimModel): string[] => {
    let maxId = null;
    if (claim.claimServiceEarnings) {
        if (claim.grantsReceived && claim.grantsReceivedCount > 1) {
            let maxValue = 0;
            for (let e of claim.servicesIPRKnowHowEntries) {
                if (e.earningsReceivedMonth != null && e.date == null) continue;
                let amount = calculateServicesIPRKnowHowAmount(e);
                if (e.date != null && maxId == null || (amount > maxValue)) {
                    maxValue = amount;
                    maxId = e.id
                }
            }
        }
    }

    return maxId != null ? [maxId] : [];
}

const reducer9C = (claim: ClaimModel): string[] => {
    let maxId = null;
    if (claim.claimTourismEarnings) {
        if (claim.grantsReceived && claim.grantsReceivedCount > 1) {
            let maxValue = 0;
            for (let e of claim.tourismServicesEntries) {
                if (e.earningsReceivedMonth != null && e.date == null) continue;
                let amount = calculateTourismServicesAmount(e);
                if (e.date != null && maxId == null || (amount > maxValue)) {
                    maxValue = amount;
                    maxId = e.id
                }
            }
        }
    }

    return maxId != null ? [maxId] : [];
}

export const CODE_INFECTED = "infected";

export const documentTypeInfo: DocumentTypeInfo[] = [
    //Declaration
    {
        code: "applicant_declaration",
        reducer: m => [""],
        title: m => "Applicant Declaration",
        description: m => `Applicant declaration ${m.applicantFirstName ? "signed by" : ""} ${m.applicantFirstName || ''} ${m.applicantLastName || ''}`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["applicant_declaration", "applicant_and_consultant_declaration"]
    },
    {
        code: "consultant_declaration",
        reducer: m => m.isConsultant ? [""] : [],
        title: m => "Consultant Declaration",
        description: m => "Signed Consultant Declaration",
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["consultant_declaration", "applicant_and_consultant_declaration"]
    },

    //Balance sheet/Profit and Loss
    {
        code: "profit_and_loss_current",
        reducer: m => requirePLAndBSCurrent(m) ? [""] : [],
        title: m => `Profit and Loss Statement (${getFY(m.claimYear)})`,
        description: m => `Profit and Loss Statement (${getFY(m.claimYear)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["profit_and_loss", "balance_sheet_and_profit_loss"]
    },
    {
        code: "balance_sheet_current",
        reducer: m => requirePLAndBSCurrent(m) ? [""] : [],
        title: m => `Balance Sheet (${getFY(m.claimYear)})`,
        description: m => `Balance Sheet (${getFY(m.claimYear)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["balance_sheet", "balance_sheet_and_profit_loss"]
    },
    {
        code: "profit_and_loss_previous",
        reducer: m => m.grantsReceived === false && m.claimTwoYears ? [""] : [],
        title: m => `Profit and Loss Statement (${getFY(m.claimYear - 1)})`,
        description: m => `Profit and Loss Statement (${getFY(m.claimYear - 1)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["profit_and_loss", "balance_sheet_and_profit_loss"]
    },
    {
        code: "balance_sheet_previous",
        reducer: m => m.grantsReceived === false && m.claimTwoYears ? [""] : [],
        title: m => `Balance Sheet (${getFY(m.claimYear - 1)})`,
        description: m => `Balance Sheet (${getFY(m.claimYear - 1)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["balance_sheet", "balance_sheet_and_profit_loss"]
    },

    //Category is Approved body or Approved Joint Venture
    {
        code: "approved_status_required",
        reducer: m => requireApprovalStatusDocument(m) ? [""] : [],
        title: m => "Approved Status Application (Optional)",
        description: m => "Approved Status Application (Optional)",
        required: m => false,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null
    },

    //Goods not made in  Australia
    {
        code: "goods_not_made_in_australia",
        reducer: m => m.promotingGoods && !m.goodsManufacturedInAustralia ? [""] : [],
        title: m => "S24(b) Submission",
        description: m => "S24(b) Submission",
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["goods_not_made_in_australia"]
    },

    //Export principal
    {
        code: "international_sales_invoice_or_contract_in_relation_to_the_grant_year",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.InternationalSalesInvoiceOrContractInRelationToGrantYear ? [""] : [],
        title: m => `International sales invoice or contract in relation to ${getFY(m.claimYear)} grant year`,
        description: m => `International sales invoice or contract in relation to ${getFY(m.claimYear)} grant year`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["invoice", "contract_agreement"]
    },
    {
        code: "international_sales_invoice_or_contract_post_the_grant_year",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.InternationalSalesInvoiceOrContractPostGrantYear ? [""] : [],
        title: m => `International sales invoice or contract post the ${getFY(m.claimYear)} grant year`,
        description: m => `International sales invoice or contract post the ${getFY(m.claimYear)} grant year`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["invoice", "contract_agreement"]
    },
    {
        code: "domestic_sales_invoice_in_relation_to_the_grant_year",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.DomesticSalesInvoice ? [""] : [],
        title: m => `Domestic sales invoice in relation to ${getFY(m.claimYear)} grant year`,
        description: m => `Domestic sales invoice in relation to ${getFY(m.claimYear)} grant year`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "bank_statement_showing_payment",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.BankStatementShowingPayment ? [""] : [],
        title: m => 'Bank statement showing payment',
        description: m => 'Bank statement showing payment',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },
    {
        code: "contract",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.Contract ? [""] : [],
        title: m => 'Contract',
        description: m => 'Contract',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["contract_agreement"]
    },
    {
        code: "other_evidence_of_IP_registration_or_AusIndustry_grant",
        reducer: m => requirePrincipalAdditionalDocument(m) && m.principalAdditionalDocument == PrincipalAdditionalDocument.Other ? [""] : [],
        title: m => 'Other - e.g. Evidence of IP registration or AusIndustry grant',
        description: m => 'Other - e.g. Evidence of IP registration or AusIndustry grant',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },

    //Performance measure = Opion B
    {
        code: "audited_statement_of_Income_current",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => `Audited Statement of Income (${getFY(m.claimYear)})`,
        description: m => `Audited Statement of Income (${getFY(m.claimYear)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },
    {
        code: "audited_profit_and_loss_statement_current",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => `Audited Profit and Loss Statement (${getFY(m.claimYear)})`,
        description: m => `Audited Profit and Loss Statement (${getFY(m.claimYear)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["profit_and_loss", "balance_sheet_and_profit_loss"]
    },
    {
        code: "audited_profit_and_loss_statement_previous",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => `Audited Profit and Loss Statement (${getFY(m.claimYear - 1)})`,
        description: m => `Audited Profit and Loss Statement (${getFY(m.claimYear - 1)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["profit_and_loss", "balance_sheet_and_profit_loss"]
    },
    {
        code: "audited_balance_sheet_current",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => `Audited Balance Sheet (${getFY(m.claimYear)})`,
        description: m => `Audited Balance Sheet (${getFY(m.claimYear)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["balance_sheet", "balance_sheet_and_profit_loss"]
    },
    {
        code: "audited_balance_sheet_previous",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => `Audited Balance Sheet (${getFY(m.claimYear - 1)})`,
        description: m => `Audited Balance Sheet (${getFY(m.claimYear - 1)})`,
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: ["balance_sheet", "balance_sheet_and_profit_loss"]
    },
    {
        code: "business_plan_including_international_marketing_strategy",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => 'Business plan, including international marketing strategy',
        description: m => 'Business plan, including international marketing strategy',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },
    {
        code: "economic_benefits_statement",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => 'Economic benefits statement',
        description: m => 'Economic benefits statement',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },
    {
        code: "independent_opinion_of_applicants_export_potential",
        reducer: m => requireOptionBDocuments(m) ? [""] : [],
        title: m => "Independent opinion of applicant's export potential",
        description: m => "Independent opinion of applicant's export potential",
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },
    //Deed document for trust
    {
        code: "trust_deed_document",
        reducer: m => requireTrustDeedDocument(m) ? [""] : [],
        title: m => 'Trust Deed Document',
        description: m => 'Trust Deed Document',
        required: m => true,
        quickList: m => true,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },

    //QIP consultant - Reportable Eligibility Statement
    {
        code: "reportable_eligibility_statement",
        reducer: m => m.isQIPConsultant ? [""] : [],
        title: m => 'Reportable Eligibility Statement (Optional)',
        description: m => 'Reportable Eligibility Statement (Optional)',
        required: m => false,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: null, //null to ignore scoring and not validate
        moreInfo: `<p>Only Quality Incentive Program Consultants are to complete this document if there is an eligibility point they may wish to explain or expand upon.</p>\n <a href="https://www.austrade.gov.au/ArticleDocuments/6917/Reportable-EMDG-Grant-Eligibility-Position-Disclosure-form.dotx.aspx" target="_blank">Click here</a> to download the EMDG Grant Eligibility document. `
    },

    //Schedule 1A
    {
        code: "1A_signed_agreement",
        reducer: (m, lowRiskMarkets) => reducer1A(m, lowRiskMarkets),
        title: (m, k) => {
            let osRep = m.representations.filter(r => r.id == k)[0];
            return `Schedule 1A: Signed agreement with ${osRep.firstName} ${osRep.lastName}`
        },
        description: (m, k) => {
            let osRep = m.representations.filter(r => r.id == k)[0];
            return `Schedule 1A: Signed agreement covering the grant year with overseas representative ${osRep.firstName} ${osRep.lastName}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["contract_agreement"]
    },
    {
        code: "1A_evidence_of_payment",
        reducer: (m, lowRiskMarkets) => reducer1A(m, lowRiskMarkets, true),
        title: (m, k) => {
            let parts = k.split("|");

            if (parts.length < 2)
                return "Schedule 1A: Evidence of payment to overseas representative";

            let osRep = m.representations.filter(r => r.id == parts[0])[0];
            let entry = osRep.entries.filter(e => e.id == parts[1])[0];

            return `Schedule 1A: Evidence of payment to ${osRep.firstName} ${osRep.lastName} for Ref No.: ${entry.reference}`
        },
        description: (m, k) => {
            let parts = k.split("|");

            if (parts.length < 2)
                return "Evidence of payment (e.g. bank statement) for the largest value expenditure item";

            let osRep = m.representations.filter(r => r.id == parts[0])[0];
            let entry = osRep.entries.filter(e => e.id == parts[1])[0];

            return `Evidence of payment (e.g. bank statement) to overseas representative ${osRep.firstName} ${osRep.lastName} for Ref No.: ${entry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },
    {
        code: "1A_email_correspondence",
        reducer: (m, lowRiskMarkets) => reducer1A(m, lowRiskMarkets),
        title: (m, k) => {
            let osRep = m.representations.filter(r => r.id == k)[0];
            return `Schedule 1A: e-mail between ${osRep.firstName} ${osRep.lastName} and customer/potential customer`
        },
        description: (m, k) => {
            let osRep = m.representations.filter(r => r.id == k)[0];
            return `Schedule 1A: e-mail between overseas representative ${osRep.firstName} ${osRep.lastName} and customer/potential customer`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["email_correspondence"]
    },

    //Schedule 1B
    {
        code: "1B_written_agreement",
        reducer: m => reducer1B(m),
        title: (m, k) => {
            let consultant = m.marketingConsultants.filter(m => m.id == k)[0];
            return `Schedule 1B: Written agreement with ${consultant.firstName} ${consultant.lastName}`
        },
        description: (m, k) => {
            let consultant = m.marketingConsultants.filter(m => m.id == k)[0];
            return `Schedule 1B: Written agreement between applicant and marketing consultant (${consultant.firstName} ${consultant.lastName})`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["contract_agreement"]
    },
    {
        code: "1B_reports_or_emails",
        reducer: m => reducer1B(m),
        title: (m, k) => {
            let consultant = m.marketingConsultants.filter(m => m.id == k)[0];
            return `Schedule 1B: 3 Reports or emails for marketing consultant ${consultant.firstName} ${consultant.lastName}`
        },
        description: (m, k) => {
            let consultant = m.marketingConsultants.filter(m => m.id == k)[0];
            return `Schedule 1B: 3 Reports or emails between applicant and marketing consultant (${consultant.firstName} ${consultant.lastName})`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["email_correspondence", "report_work"]
    },

    //Schedule 2
    {
        code: "2_travel_invoice",
        reducer: (m) => reducer2(m),
        title: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Travel invoice for trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        description: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Travel invoice for marketing visit, trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "2_evidence_of_payment",
        reducer: (m) => reducer2(m),
        title: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Evidence of payment for trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        description: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Evidence of payment (e.g. bank statement) for marketing visit, trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },
    {
        code: "2_trip_diary",
        reducer: (m) => reducer2(m),
        title: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Trip diary for trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        description: (m, k) => {
            let mv = m.marketingVisitEntries.filter(m => m.id == k)[0];
            return `Schedule 2: Trip diary detailing daily activities for the duration of the trip for marketing visit, trip number: ${mv.tripNo} and reference: ${mv.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["travel_diary"]
    },

    //Schedule 4
    {
        code: "4_free_of_charge_invoice",
        reducer: (m) => reducer4(m),
        title: (m, k) => {
            let fs = m.freeSampleEntries.filter(m => m.id == k)[0];
            return `Schedule 4: Free of charge invoice reference: ${fs.reference}`
        },
        description: (m, k) => {
            let fs = m.freeSampleEntries.filter(m => m.id == k)[0];
            return `Schedule 4: Free of charge invoice for free sample, reference: ${fs.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "4_cost_analysis",
        reducer: (m) => reducer4(m),
        title: (m, k) => {
            let fs = m.freeSampleEntries.filter(m => m.id == k)[0];
            return `Schedule 4: Cost analysis to support unit cost of item reference: ${fs.reference}`
        },
        description: (m, k) => {
            let fs = m.freeSampleEntries.filter(m => m.id == k)[0];
            return `Schedule 4: Cost analysis to support the unit cost of item for free sample, reference: ${fs.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },

    //Schedule 6
    {
        code: "6_invoice",
        reducer: (m) => reducer6(m),
        title: (m, k) => {
            let fs = m.advertisingEntries.filter(m => m.id == k)[0];
            return `Schedule 6: Invoice reference: ${fs.reference}`
        },
        description: (m, k) => {
            let fs = m.advertisingEntries.filter(m => m.id == k)[0];
            return `Schedule 6: Invoice for promotional literature and advertising, reference: ${fs.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "6_evidence_of_payment",
        reducer: (m) => reducer6(m),
        title: (m, k) => {
            let fs = m.advertisingEntries.filter(m => m.id == k)[0];
            return `Schedule 6: Evidence of payment reference: ${fs.reference}`
        },
        description: (m, k) => {
            let fs = m.advertisingEntries.filter(m => m.id == k)[0];
            return `Schedule 6: Evidence of payment (e.g. bank statement) for promotional literature and advertising, reference: ${fs.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },

    //Schedule 10A
    {
        code: "10A_invoice",
        reducer: m => reducer9A(m),
        title: (m, k) => {
            let goodsEntry = m.goodsEntries.filter(m => m.id == k)[0];
            return `Schedule 10A: Invoice/shipment no.: ${goodsEntry.reference}`
        },
        description: (m, k) => {
            let goodsEntry = m.goodsEntries.filter(m => m.id == k)[0];
            return `Schedule 10A: Invoice for goods earnings, invoice/shipment no.: ${goodsEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "10A_bank_statement",
        reducer: m => reducer9A(m),
        title: (m, k) => {
            let goodsEntry = m.goodsEntries.filter(m => m.id == k)[0];
            return `Schedule 10A: Bank statement invoice/shipment no.: ${goodsEntry.reference}`
        },
        description: (m, k) => {
            let goodsEntry = m.goodsEntries.filter(m => m.id == k)[0];
            return `Schedule 10A: Bank statement showing receipt of goods earnings, invoice/shipment no.: ${goodsEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },

    //Schedule 10B
    {
        code: "10B_invoice",
        reducer: m => reducer9B(m),
        title: (m, k) => {
            let serviceIPRKnowHowEntry = m.servicesIPRKnowHowEntries.filter(m => m.id == k)[0];
            return `Schedule 10B: Invoice/statement no.: ${serviceIPRKnowHowEntry.reference}`
        },
        description: (m, k) => {
            let serviceIPRKnowHowEntry = m.servicesIPRKnowHowEntries.filter(m => m.id == k)[0];
            return `Schedule 10B: Invoice for services/IPR/know how earnings, invoice/statement no.: ${serviceIPRKnowHowEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "10B_bank_statement",
        reducer: m => reducer9B(m),
        title: (m, k) => {
            let serviceIPRKnowHowEntry = m.servicesIPRKnowHowEntries.filter(m => m.id == k)[0];
            return `Schedule 10B: Banks statement invoice/statement no.: ${serviceIPRKnowHowEntry.reference}`
        },
        description: (m, k) => {
            let serviceIPRKnowHowEntry = m.servicesIPRKnowHowEntries.filter(m => m.id == k)[0];
            return `Schedule 10B: Banks statement showing receipt of services/IPR/know how earnings, invoice/statement no.: ${serviceIPRKnowHowEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },

    //Schedule 10C
    {
        code: "10C_invoice",
        reducer: m => reducer9C(m),
        title: (m, k) => {
            let tourismServicesEntry = m.tourismServicesEntries.filter(m => m.id == k)[0];
            return `Schedule 10C: Invoice no.: ${tourismServicesEntry.reference}`
        },
        description: (m, k) => {
            let tourismServicesEntry = m.tourismServicesEntries.filter(m => m.id == k)[0];
            return `Schedule 10C: Invoice for tourism services earnings, invoice no.: ${tourismServicesEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["invoice"]
    },
    {
        code: "10C_bank_statement",
        reducer: m => reducer9C(m),
        title: (m, k) => {
            let tourismServicesEntry = m.tourismServicesEntries.filter(m => m.id == k)[0];
            return `Schedule 10C: Bank statement invoice no.: ${tourismServicesEntry.reference}`
        },
        description: (m, k) => {
            let tourismServicesEntry = m.tourismServicesEntries.filter(m => m.id == k)[0];
            return `Schedule 10C: Bank statement showing receipt of tourism services earnings, invoice no.: ${tourismServicesEntry.reference}`
        },
        required: m => true,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: ["bank_statements"]
    },

    //Other
    {
        code: "other_documents",
        reducer: m => [""],
        title: m => "Other documents",
        description: m => "Other documents",
        required: m => false,
        quickList: m => false,
        multipleFiles: m => true,
        mlClasses: null //null to ignore scoring and not validate
    },
];
